import Navigation from "./Navigation/Navigation.tsx";
import React from "react";
import MainHeaderPage from "./Header/MainHeaderPage.tsx";
import {useAppSelector} from "../../hooks/reduxHooks.ts";
import GlobalLoadPage from "./GlobalLoadPage.tsx";
import i18n from "i18next";
import {ErrorBoundary} from "react-error-boundary";
import ErrorFallback from "../error/ErrorFallback";
import {RequiresDpa} from "../rights/RequiresDpa.tsx";
import useDirtyBlockerReset from "../../hooks/useDirtyBlockerReset.ts";
import SupportContextBanner from "../supportContext/SupportContextBanner.tsx";
import {devError} from "../../utils/dev.ts";
import MailServerHealthBanner from "./MailServerHealthBanner.tsx";
import {useMailServerHealthWatch} from "../../hooks/useMailServerHealth.ts";

export const CONTROLS_PORTAL_ID = "basePageControlsPortalId";

export interface BasePageProps {
    HeaderPageComponent?: React.FC<{ title?: string, eyebrow?: string, buttons?: React.ReactNode, noSupportContextPicker?: boolean }>;
    NavigationComponent?: React.FC;
    /**
     * Render output for the main content area.
     *
     * Pass a ReactNode (`<MyMain .../>`), NOT an inline arrow returning a node.
     * An inline arrow creates a new function reference per render, which used
     * to remount the inner subtree on every parent render and silently lose
     * its useState. ReactNode keeps stable identity.
     */
    mainContent: React.ReactNode;
    title?: string;
    /** Small line above the title, naming the area the titled tab belongs to. */
    eyebrow?: string;
    RightHeaderComponent?: React.ReactNode;
    noControls?: boolean;
    noDpaProtection?: boolean;
    // for pages that bring their own, larger support context search
    noSupportContextPicker?: boolean;
}

const BasePage = ({
                      HeaderPageComponent = MainHeaderPage,
                      NavigationComponent = Navigation,
                      mainContent,
                      title,
                      eyebrow,
                      RightHeaderComponent,
                      noControls,
                      noDpaProtection,
                      noSupportContextPicker,
                  }: BasePageProps) => {
    const myAccount = useAppSelector(state => state.auth.myAccount);

    const [error, setError] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!myAccount?.userDetails?.language) return;
        setError(false);
        i18n.changeLanguage(myAccount.userDetails.language.isoCode2)
            .catch(err => {
                devError("Error changing language:", err);
                setError(true);
            });
    }, [myAccount?.userDetails.language]);

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useDirtyBlockerReset();
    useMailServerHealthWatch();

    if (error) {
        return <div className="flex flex-col items-center justify-center h-screen bg-primary">
            <h1 className="an-h1 text-white">{i18n.t("error.languageChangeFailed")}</h1>
        </div>;
    }

    if (!myAccount) return <GlobalLoadPage/>;

    return (
        <div className="box-border p-2 sm:p-6 h-screen overflow-hidden">
            <div className="box-border an-app-island flex gap-2 sm:gap-6 p-2 sm:p-6 pr-0  h-full">
                <NavigationComponent/>
                <div className="box-border flex-1 flex flex-col min-w-0 min-h-0">
                    <SupportContextBanner/>
                    {/* Above title and tabs: it is the most urgent thing on any page (#55819). */}
                    <MailServerHealthBanner className="mx-2 sm:mx-4"/>
                    <HeaderPageComponent title={title} eyebrow={eyebrow} buttons={RightHeaderComponent} noSupportContextPicker={noSupportContextPicker}/>
                    <div className="box-border flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden px-2 sm:px-4 pb-4">
                        <ErrorBoundary fallbackRender={({error}) => <ErrorFallback error={error as Error} showBaseNotice/>}>
                            <div className={"flex flex-col h-full w-full relative"}>
                                <RequiresDpa disable={noDpaProtection}>
                                    {mainContent}
                                </RequiresDpa>
                            </div>
                        </ErrorBoundary>
                    </div>
                    {!noControls && (
                        <div
                            id={CONTROLS_PORTAL_ID}
                            className={"flex flex-row flex-wrap items-center content-center min-h-[7em] bg-an-surface z-10 justify-between gap-3 px-6 pt-6 empty:hidden"}
                            style={{borderTop: "1px solid var(--an-border)"}}
                        />
                    )}
                </div>
                <div id="asideroot"/>
            </div>
        </div>
    );
};

export default BasePage;
